/**
 * EditableSqlValue — click-to-edit SQL value text: a button showing `text`
 * that turns into a `SqlValueInput` (suggestions from `getSuggestions`, built
 * only while editing). Enter or blur commits a changed, non-empty value
 * through `onCommit`; Esc — or an unchanged / emptied value — reverts. The
 * input opens with its text selected. `trailing` (e.g. a comment) stays
 * beside it in both states; `wrapDisplay` can wrap the button (a tooltip).
 */

import { useEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';

import type { SqlValueSuggestion } from '../utils/sqlValueSuggestions';
import { SqlValueInput } from './SqlValueInput';

export interface EditableSqlValueProps {
  readonly text: string;
  /** The trimmed new value — only when it changed. */
  readonly onCommit: (value: string) => void;
  readonly getSuggestions?: () => readonly SqlValueSuggestion[];
  readonly testId: string;
  readonly inputTestId: string;
  readonly ariaLabel: string;
  readonly className?: string;
  readonly inputClassName?: string;
  /** Extra attributes on the display button (`data-cell-kind`). */
  readonly buttonProps?: HTMLAttributes<HTMLButtonElement> & Record<`data-${string}`, string>;
  /** Extra attributes on the input (an escape-claim marker). */
  readonly inputProps?: Record<string, string>;
  readonly wrapDisplay?: (button: ReactNode) => ReactNode;
  /** When set, both states render inside a span with this class, `trailing` after. */
  readonly wrapperClassName?: string;
  readonly trailing?: ReactNode;
  readonly trackEvent?: string;
}

export function EditableSqlValue({
  text,
  onCommit,
  getSuggestions,
  testId,
  inputTestId,
  ariaLabel,
  className,
  inputClassName,
  buttonProps,
  inputProps,
  wrapDisplay = (button) => button,
  wrapperClassName,
  trailing,
  trackEvent,
}: EditableSqlValueProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestions = useMemo(() => (editing && getSuggestions ? getSuggestions() : []), [editing, getSuggestions]);

  useEffect(() => {
    if (!editing) setValue(text);
  }, [text, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = (): void => {
    const next = value.trim();
    setEditing(false);
    if (!next || next === text.trim()) setValue(text);
    else onCommit(next);
  };
  const cancel = (): void => {
    setEditing(false);
    setValue(text);
  };

  const body = editing ? (
    <SqlValueInput
      {...inputProps}
      ref={inputRef}
      value={value}
      onValueChange={setValue}
      suggestions={suggestions}
      onBlur={commit}
      onSubmit={commit}
      onCancel={cancel}
      testId={inputTestId}
      className={inputClassName}
      aria-label={ariaLabel}
      trackEvent={trackEvent}
    />
  ) : (
    wrapDisplay(
      <button {...buttonProps} type="button" className={className} data-testid={testId} onClick={() => setEditing(true)}>
        {text}
      </button>,
    )
  );
  return wrapperClassName ? (
    <span className={wrapperClassName}>
      {body}
      {trailing}
    </span>
  ) : (
    body
  );
}
